<template>
    <div class="EoPVerifications">
        <div v-if="isLoading" class="content-container">
            <div class="loading">
                <span class="loader"></span>
            </div>
        </div>
        <div v-else class="content-container">
            <div class="top-content">
                <div class="flex-row-centered">
                    <MarketSelector @change=updateSelection enableSE />
                    <PeriodSelector v-model=periodSelection showTitle @update:model-value=updateSelection />
                </div>
            </div>
            <Table
                :Rows=displayedEoPVerificationsList
                :Cols=cols
                :HasError=infoError
                v-model:Page=page
                v-model:RowsPerPage=rowCount
                :Labels=labels
                :DefaultSortCols=defaultSortCols
                :DefaultSortOrders=defaultSortOrders
            >
                <template #EoPStatus="props">
                    <div v-if="props.value.EoPStatus === 'SUCCESS'" class="highlight-green-bold">
                        {{ props.value.EoPStatus }}
                    </div>
                    <div v-if="props.value.EoPStatus === 'ERROR'" class="failure">
                        {{ props.value.EoPStatus }}
                    </div>
                </template>
                <template #Category-1="props">
                    <button
                        @click="ViewEoPVerificationReport(props.value.EoPType, props.value.EoPType)"
                        :disabled="DenyFunctionLevelAccess(FunctionAccessLevels.ViewEoPVReport)"
                        :title="DenyFunctionLevelAccess(FunctionAccessLevels.ViewEoPVReport) ? `View ${props.value.JournalingStatus} report (Access denied)` : `View ${props.value.JournalingStatus} report (opens new page)`"
                        class="table-button"
                    >
                        Report
                    </button>
                </template>
            </Table>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    onMounted,
    onBeforeMount,
    inject,
} from "vue";
import { onBeforeRouteLeave } from "vue-router";
import router from "@/router";
// Services
import { apiLoggerKey } from "@/types/ServiceKeys";
import {
    cols,
    labels,
    parseData,
    defaultSortCols,
    defaultSortOrders,
} from "@/services/helpers/EoPVerificationsHelper";
import { FunctionAccessLevels, DenyFunctionLevelAccess } from "@/config/AuthConfig";
// Data
import { MarketSelection, marketSelection } from "@/services/helpers/Helper";
// Table
import Table from "@/components/TablePersistentChecking.vue";
// Misc
import log from "loglevel";
import ErrorInfo from "@/components/ErrorInfo.vue";
import { IEoPItem, IEoPVerifications, ICache } from "@/Interfaces/EoPVerifications/IEoPVerifications";
import DatePeriod from "@/types/DatePeriod";
import MarketSelector from "@/components/MarketSelector.vue";
import PeriodSelector from "@/components/PeriodSelector.vue";
import { eoPVerificationHeader } from "@/services/network";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";

const page = ref(1);
const rowCount = ref(40);

const fileName = "EoPVerifications.vue";
const Logger = inject(apiLoggerKey);

////////////////////////////////////////////////////////////////////////////
// LOGIC
////////////////////////////////////////////////////////////////////////////
onBeforeRouteLeave((to, from, next) => {
    log.trace("onBeforeRouteLeave() EoPVerifications");
    if (to.name === "EoP Verification Report") {
        const cache: ICache = {
            Metadata: {
                Market: marketSelection.value,
                Period: periodSelection.value.toJSON(),
                PageNumber: page.value,
                RowCount: rowCount.value,
                SortCols: defaultSortCols.value,
                SortOrders: defaultSortOrders.value,
            },
        };
        log.debug("onBeforeRouteLeave()\ncache.Metadata", cache.Metadata);
        sessionStorage.setItem("EoPVerifications", JSON.stringify(cache));

        const periodCache = periodSelection.value;
        log.debug("onBeforeRouteLeave()\nperiodCache", periodCache);
        sessionStorage.setItem("EoPVerificationsPeriod", periodCache.toJSON());
    }
    next();
});

onBeforeMount(() => {
    log.trace("onBeforeMount() EoPVerifications");
    if (
        lastPage.value?.startsWith(EoPVerificationRoutePath!) ||
        previousPage.value?.startsWith(EoPVerificationRoutePath!)
    ) {
        if ("EoPVerifications" in sessionStorage) {
            log.debug("onBeforeMount() 'EoPVerifications' in sessionStorage");
        }
    } else {
        log.debug("onBeforeMount() removing 'EoPVerifications' from sessionStorage");
        sessionStorage.removeItem("EoPVerifications");
        sessionStorage.removeItem("EoPVerificationsPeriod");
    }
});

const EoPVerificationRoutePath = router.options.routes
    .find((route) => route.name === "EoP Verification Report")
    ?.path.split("/:")[0];
const previousPage = ref(router.options.history.state.back?.toString());
const lastPage = ref(router.options.history.state.forward?.toString());

onMounted(() => {
    log.debug("onMounted() EoPVerifications");
    document.title = 'EoP Verifications - UV'
    GetEoPVerifications();
});
// Get EoPVerifications data from API
async function GetEoPVerifications() {
    const name = GetEoPVerifications.name;
    const Class = fileName + "/" + name;
    infoError.value = false;
    const raw = sessionStorage.getItem("EoPVerifications");
    log.debug("Loading EoPVerification Reports from session storage", raw);
    if (raw) {
        const cache: ICache = JSON.parse(raw);
        const metadata = cache.Metadata;
        marketSelection.value = (Object.keys(MarketSelection) as (keyof typeof MarketSelection)[]).find(
            m => m === metadata.Market
        ) ?? 'GB';
        periodSelection.value = new DatePeriod(metadata.Period);
        if (!periodSelection.value.isValid()) console.trace('periodSelection should not be invalid');

        page.value = metadata.PageNumber!;
        rowCount.value = metadata.RowCount!;
        defaultSortCols.value = metadata.SortCols!;
        defaultSortOrders.value = metadata.SortOrders!;
        const market = marketSelection.value;
        const period = periodSelection.value;
        const data = (await eoPVerificationHeader.get({market: market.toString(), period: period  })).data;
        EoPVerifications.value = data;
        displayedEoPVerificationsList.value = parseData(data);
        
        isLoading.value = false;
    } else {
        const periodStr = sessionStorage.getItem("EoPVerificationsPeriod");
        if (periodStr) periodSelection.value = new DatePeriod(periodStr);
        const market = marketSelection.value;
        const period = periodSelection.value;
        var Message = `Getting EoPVerifications (market: ${market}, period: ${period})`;
        log.debug("Loading EoPVerification Reports from API");
        isLoading.value = true;
        displayedEoPVerificationsList.value = undefined;
        if (!period.isValid()) console.trace('API periodSelection should not be invalid');
        try {
            const data = (await eoPVerificationHeader.get({market: market.toString(), period: period  })).data;
            EoPVerifications.value = data;
            displayedEoPVerificationsList.value = parseData(data);
            Logger?.LogInformation(Message, Class);
        } catch (error) {
            infoError.value = true;
            useNotifierWithErrFormatter().error({error, errorType: "Getting EoP Verifications"})
            Message = `(Error) ${Message}, error: ${error}`;
            log.error(Message);
            Logger?.LogError(Message, Class);
        } finally {
            isLoading.value = false;
        }
    }    
}
//Route to the EoP Verification Report page with the selected Journal Type and parameters
function ViewEoPVerificationReport(JournalType: string, DisplayType: string) {
    log.trace("ViewEoPVerificationReport()\nId:", JournalType);
    const r = {
        name: "EoP Verification Report",
        params: {
            id: JournalType,
        },
        query: {
            market: marketSelection.value,
            period: periodSelection.value.toJSON(),
            type: DisplayType
        },
    };
    router.push(r);
}

// ERROR
const GettingEoPVerificationsErrorInfo = ErrorInfo;
const infoError = ref(false);

// TABLE
const isLoading = ref(true);
const EoPVerifications = ref<IEoPVerifications>();
const displayedEoPVerificationsList = ref<IEoPItem[]>();

// MISC
const periodSelection = ref(new DatePeriod(new Date().addMonths(-1)));

function updateSelection() {
    log.trace("updateSelection()");

    log.debug("Removing EoPVerifications and EoPVerificationsPeriod from sessionStorage");
    sessionStorage.removeItem("EoPVerifications");
    sessionStorage.removeItem("EoPVerificationsPeriod");

    const name = updateSelection.name;
    const Message = `Changed selection to: market: ${marketSelection.value}; period: ${periodSelection.value}.`;
    const Class = fileName + "/" + name;

    isLoading.value = true;
    GetEoPVerifications();
    Logger?.LogInformation(Message, Class);
}
</script>
